import React from 'react'
import { motion } from "framer-motion"
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'

export const Terms = ({ language }) => {
    const isAr = language === 'ar'

    const fadeUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
    }

    const sections = [
        { id: 'definitions', title: isAr ? '1. التعريفات والتفسير' : '1. Interpretation & Definitions', icon: 'lucide:book-open' },
        { id: 'acknowledgment', title: isAr ? '2. الإقرار والموافقة' : '2. Acknowledgment', icon: 'lucide:check-circle' },
        { id: 'services', title: isAr ? '3. الملكية الفكرية والخدمات' : '3. Intellectual Property & Services', icon: 'lucide:code-2' },
        { id: 'thirdparty', title: isAr ? '4. روابط الجهات الخارجية' : '4. Links to Other Websites', icon: 'lucide:external-link' },
        { id: 'termination', title: isAr ? '5. الإنهاء' : '5. Termination', icon: 'lucide:slash' },
        { id: 'liability', title: isAr ? '6. حدود المسؤولية' : '6. Limitation of Liability', icon: 'lucide:shield-alert' },
        { id: 'disclaimer', title: isAr ? '7. إخلاء المسؤولية' : '7. "AS IS" Disclaimer', icon: 'lucide:alert-circle' },
        { id: 'governing', title: isAr ? '8. القانون الحاكم وحل النزاعات' : '8. Governing Law & Disputes', icon: 'lucide:scale' },
        { id: 'changes', title: isAr ? '9. تعديل الشروط' : '9. Changes to Terms', icon: 'lucide:refresh-cw' },
        { id: 'contact', title: isAr ? '10. معلومات التواصل' : '10. Contact Information', icon: 'lucide:mail' }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full min-h-screen bg-[#f8fafc] dark:bg-[#07070f] text-slate-800 dark:text-gray-200 transition-colors duration-300"
        >
            {/* ── HERO HEADER ────────────────────────────────────────── */}
            <div className="relative overflow-hidden border-b border-slate-200 dark:border-white/10"
                style={{
                    background: 'linear-gradient(180deg, rgba(82,92,235,0.08) 0%, rgba(0,210,255,0.03) 100%)'
                }}>
                <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #525ceb, #00d2ff)' }} />

                <div className="max-w-6xl mx-auto px-4 md:px-10 lg:px-20 py-12 md:py-16 relative z-10">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-6">
                        <Link to="/" className="hover:text-bluetheme transition-colors flex items-center gap-1">
                            <Icon icon="lucide:arrow-left" className={isAr ? "rotate-180" : ""} />
                            {isAr ? 'الرئيسية' : 'Home'}
                        </Link>
                        <span>/</span>
                        <span className="text-bluetheme">{isAr ? 'الشروط والأحكام' : 'Terms & Conditions'}</span>
                    </div>

                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 bg-bluetheme/10 text-bluetheme border border-bluetheme/20">
                        <Icon icon="lucide:scale" />
                        {isAr ? 'وثيقة قانونية' : 'Legal Documentation'}
                    </div>

                    <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight mb-4">
                        {isAr ? 'الشروط والأحكام' : 'Terms & Conditions'}
                    </h1>

                    <p className="text-sm md:text-base text-slate-600 dark:text-gray-400 max-w-2xl leading-relaxed">
                        {isAr
                            ? 'يرجى قراءة هذه الشروط والأحكام بعناية قبل استخدام موقعنا وخدمات Torky Studio. وصولك إلى الخدمة يعني موافقتك الكاملة على هذه الشروط.'
                            : 'Please read these terms and conditions carefully before using our website and Torky Studio services. Your access and use of the Service signifies your full agreement.'}
                    </p>

                    <div className="flex items-center gap-4 mt-6 text-xs text-slate-500 dark:text-gray-500 font-mono">
                        <span className="flex items-center gap-1">
                            <Icon icon="lucide:clock" /> {isAr ? 'آخر تحديث: يناير 2025' : 'Last updated: January 2025'}
                        </span>
                        <span>•</span>
                        <span>Torky Studio · Mohamed Torky</span>
                    </div>
                </div>
            </div>

            {/* ── CONTENT GRID ───────────────────────────────────────── */}
            <div className="max-w-6xl mx-auto px-4 md:px-10 lg:px-20 py-12 md:py-16">
                <div className="flex flex-col lg:flex-row gap-10">

                    {/* Table of Contents Sticky Sidebar (Desktop) */}
                    <aside className="lg:w-64 flex-shrink-0">
                        <div className="sticky top-28 p-5 rounded-2xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <h2 className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-gray-500 mb-4 font-mono">
                                {isAr ? '// الفهرس' : '// On this page'}
                            </h2>
                            <nav className="space-y-1">
                                {sections.map(s => (
                                    <a
                                        key={s.id}
                                        href={`#${s.id}`}
                                        className="flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-xl text-slate-600 dark:text-gray-400 hover:text-bluetheme hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                                    >
                                        <Icon icon={s.icon} className="text-sm opacity-60 flex-shrink-0" />
                                        <span className="truncate">{s.title}</span>
                                    </a>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    {/* Main Document Body */}
                    <main className="flex-1 space-y-8 text-start">

                        {/* Section 1: Definitions */}
                        <motion.section id="definitions" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-bluetheme mb-3">
                                <Icon icon="lucide:book-open" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '1. التفسير والتعريفات' : '1. Interpretation and Definitions'}
                                </h2>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-400 mb-5">
                                {isAr
                                    ? 'تحدد هذه البنود معاني الكلمات ذات الحروف الكبيرة، وتسري نفس التعريفات سواء وردت بصيغة المفرد أو الجمع:'
                                    : 'The words of which the initial letter is capitalized have meanings defined under the following conditions:'}
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                {[
                                    { label: isAr ? 'الشركة / الاستوديو' : 'Company / Studio', desc: isAr ? 'يشير إلى Torky Studio وموقع Mohamed Torky الشخصي.' : 'Refers to Torky Studio and Mohamed Torky Portfolio.' },
                                    { label: isAr ? 'الخدمة / الموقع' : 'Service / Website', desc: isAr ? 'الموقع الإلكتروني متاح عبر mohamedtorky.online وجميع المنصات التابعة له.' : 'Refers to the website accessible from mohamedtorky.online.' },
                                    { label: isAr ? 'المستخدم / أنت' : 'You / User', desc: isAr ? 'الفرد أو الكيان الذي يتصفح الموقع أو يتعاقد على الخدمات.' : 'The individual or entity accessing or using the Service.' },
                                    { label: isAr ? 'البلد' : 'Country', desc: isAr ? 'جمهورية مصر العربية (وماليزيا لمقر الدراسة).' : 'Egypt / Malaysia jurisdiction as applicable.' },
                                ].map((item, idx) => (
                                    <div key={idx} className="p-3.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5">
                                        <div className="font-bold text-slate-900 dark:text-white mb-1">{item.label}</div>
                                        <div className="text-slate-600 dark:text-gray-400 leading-relaxed">{item.desc}</div>
                                    </div>
                                ))}
                            </div>
                        </motion.section>

                        {/* Section 2: Acknowledgment */}
                        <motion.section id="acknowledgment" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-bluetheme mb-3">
                                <Icon icon="lucide:check-circle" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '2. الإقرار والموافقة' : '2. Acknowledgment'}
                                </h2>
                            </div>
                            <div className="space-y-3 text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                <p>
                                    {isAr
                                        ? 'تحكم هذه الشروط والأحكام استخدامك لهذه الخدمة والاتفاقية المبرمة بينك وبين Torky Studio. وصولك إلى الخدمة واستخدامها مشروط بقبولك لهذه الشروط والامتثال لها.'
                                        : 'These are the Terms and Conditions governing the use of this Service and the agreement that operates between You and the Company. Your access to and use of the Service is conditioned on Your acceptance of and compliance with these Terms.'}
                                </p>
                                <p>
                                    {isAr
                                        ? 'تقر بأنك تبلغ من العمر 18 عامًا فأكثر، أو تملك موافقة الوصي القانوني. كما أن استخدامك للخدمة مشروط بقبولك لسياسة الخصوصية الخاصة بنا.'
                                        : 'By accessing or using the Service You agree to be bound by these Terms and Conditions. You represent that you are over the age of 18 or have authorized legal guardian consent.'}
                                </p>
                            </div>
                        </motion.section>

                        {/* Section 3: Intellectual Property & Services */}
                        <motion.section id="services" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-bluetheme mb-3">
                                <Icon icon="lucide:code-2" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '3. الملكية الفكرية وخدمات التصميم' : '3. Intellectual Property & Services'}
                                </h2>
                            </div>
                            <div className="space-y-3 text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                <p>
                                    {isAr
                                        ? 'جميع التصاميم، الأكواد البرمجية، الشعارات (بما في ذلك Torky Studio وشعار MT)، والمحتويات المعروضة على هذا الموقع هي ملكية حصرية لمحمد تركي ما لم يُنص صراحة على خلاف ذلك.'
                                        : 'All original code, designs, visual assets, trademarks, and branding (including Torky Studio and the MT emblem) are the exclusive intellectual property of Mohamed Torky unless explicitly agreed otherwise in a client contract.'}
                                </p>
                                <p>
                                    {isAr
                                        ? 'عند تقديم خدمات تصميم مواقع الويب للعملاء (مثل صفحات الهبوط أو دعوات الزفاف)، يتم توضيح حقوق الملكية الفكرية وتسليم الكود والتصريح في اتفاقية الخدمة المخصصة لكل مشروع.'
                                        : 'For bespoke client projects (such as custom web development or digital wedding invitation portals), intellectual property transfers and license terms are governed by the dedicated contract agreed upon for each engagement.'}
                                </p>
                            </div>
                        </motion.section>

                        {/* Section 4: Links to Other Websites */}
                        <motion.section id="thirdparty" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-bluetheme mb-3">
                                <Icon icon="lucide:external-link" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '4. الروابط إلى مواقع أخرى' : '4. Links to Other Websites'}
                                </h2>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                {isAr
                                    ? 'قد تحتوي خدمتنا على روابط لمواقع أو خدمات تابعة لجهات خارجية لا نملكها ولا نتحكم فيها (مثل منصات العرض، GitHub، LinkedIn). لا نتحمل أي مسؤولية عن محتوى أو سياسات خصوصية أي مواقع خارجية.'
                                    : 'Our Service may contain links to third-party websites or services (such as live project demos, GitHub repositories, or external services) that are not owned or controlled by Torky Studio. We assume no responsibility for the content, privacy policies, or practices of any third-party websites.'}
                            </p>
                        </motion.section>

                        {/* Section 5: Termination */}
                        <motion.section id="termination" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-bluetheme mb-3">
                                <Icon icon="lucide:slash" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '5. الإنهاء' : '5. Termination'}
                                </h2>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                {isAr
                                    ? 'يجوز لنا إنهاء أو تعليق وصولك على الفور، دون إشعار مسبق أو مسؤولية، لأي سبب كان، بما في ذلك على سبيل المثال لا الحصر خرقك لهذه الشروط والأحكام أو إساءة استخدام الموقع.'
                                    : 'We may terminate or suspend Your access immediately, without prior notice or liability, for any reason whatsoever, including without limitation if You breach these Terms and Conditions or misuse the Service.'}
                            </p>
                        </motion.section>

                        {/* Section 6 & 7: Liability & Disclaimer */}
                        <motion.section id="liability" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-bluetheme mb-3">
                                <Icon icon="lucide:shield-alert" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '6. حدود المسؤولية وإخلاء الضمان' : '6. Limitation of Liability & "AS IS"'}
                                </h2>
                            </div>
                            <div className="space-y-3 text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                <p>
                                    {isAr
                                        ? 'يتم تقديم الخدمة "كما هي" ("AS IS") و"كما هي متاحة" ("AS AVAILABLE") دون أي ضمان من أي نوع، سواء كان صريحًا أو ضمنيًا. إلى أقصى حد يسمح به القانون، لا نتحمل أي أضرار خاصة أو تبعية أو غير مباشرة.'
                                        : 'The Service is provided to You "AS IS" and "AS AVAILABLE" with all faults and defects without warranty of any kind. To the maximum extent permitted by applicable law, in no event shall the Company be liable for any special, incidental, indirect, or consequential damages.'}
                                </p>
                            </div>
                        </motion.section>

                        {/* Section 8: Governing Law */}
                        <motion.section id="governing" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-bluetheme mb-3">
                                <Icon icon="lucide:scale" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '8. القانون الحاكم وحل النزاعات' : '8. Governing Law & Dispute Resolution'}
                                </h2>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                {isAr
                                    ? 'تخضع هذه الشروط لقوانين جمهورية مصر العربية. في حال وجود أي نزاع، فإنك توافق على محاولة تسوية النزاع بشكل ودي أولاً عن طريق التواصل المباشر مع الاستوديو.'
                                    : 'The laws of Egypt shall govern these Terms. If You have any concern or dispute about the Service, You agree to first try to resolve the dispute informally by contacting us directly.'}
                            </p>
                        </motion.section>

                        {/* Section 9: Changes */}
                        <motion.section id="changes" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-bluetheme mb-3">
                                <Icon icon="lucide:refresh-cw" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '9. تعديل الشروط' : '9. Changes to These Terms'}
                                </h2>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                {isAr
                                    ? 'نحتفظ بالحق في تعديل أو استبدال هذه الشروط في أي وقت وفقًا لتقديرنا الخاص. استمرارك في استخدام موقعنا بعد سريان التعديلات يعني موافقتك على الشروط المحدثة.'
                                    : 'We reserve the right, at Our sole discretion, to modify or replace these Terms at any time. By continuing to access or use Our Service after revisions become effective, You agree to be bound by the updated terms.'}
                            </p>
                        </motion.section>

                        {/* Section 10: Contact Us */}
                        <motion.section id="contact" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-8 rounded-3xl bg-gradient-to-br from-bluetheme/10 via-cyan-500/5 to-transparent border border-bluetheme/20 shadow-sm">
                            <div className="flex items-center gap-2 text-bluetheme mb-3">
                                <Icon icon="lucide:mail" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '10. تواصل معنا' : '10. Contact Us'}
                                </h2>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-gray-400 mb-6">
                                {isAr
                                    ? 'إذا كانت لديك أي استفسارات أو أسئلة بخصوص الشروط والأحكام، يسعدنا تواصلك معنا مباشرة عبر:'
                                    : 'If you have any questions about these Terms and Conditions, feel free to reach out directly:'}
                            </p>

                            <div className="flex flex-wrap gap-4">
                                <a
                                    href="mailto:mohamed2003torky@gmail.com"
                                    className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-white dark:bg-white/10 border border-slate-200 dark:border-white/10 text-xs font-bold hover:border-bluetheme transition-colors"
                                >
                                    <Icon icon="mdi:email" className="text-base text-red-500" />
                                    <span>mohamed2003torky@gmail.com</span>
                                </a>
                                <a
                                    href="http://wa.me/201554206775"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-white dark:bg-white/10 border border-slate-200 dark:border-white/10 text-xs font-bold hover:border-emerald-500 transition-colors"
                                >
                                    <Icon icon="ic:baseline-whatsapp" className="text-base text-emerald-500" />
                                    <span>+20 155 420 6775</span>
                                </a>
                            </div>
                        </motion.section>

                    </main>
                </div>
            </div>
        </motion.div>
    )
}
